import { Helmet } from "react-helmet-async";
import { PageHero } from "@/components/shared/PageHero";
import { ServiceSidebar } from "@/components/services/ServiceSidebar";
import { CTASection } from "@/components/shared/CTASection";
import { ServiceFAQ } from "@/components/services/ServiceFAQ";
import { snowRemovalFaqs } from "@/data/serviceFaqs";
import { Snowflake, Truck, Calendar, Shield, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import snowImage from "@/assets/services/snow-removal.jpg";

const features = [
  {
    icon: Truck,
    title: "Driveway Clearing",
    description: "Residential driveway plowing and clearing so you can get to work on time.",
  },
  {
    icon: Snowflake,
    title: "Sidewalk Service",
    description: "Complete sidewalk and walkway clearing to keep your property safe and accessible.",
  },
  {
    icon: Shield,
    title: "Ice Control",
    description: "Salt and ice melt application to prevent dangerous ice buildup.",
  },
  {
    icon: Calendar,
    title: "Seasonal Contracts",
    description: "Lock in your winter service with priority scheduling and predictable pricing.",
  },
];

const SnowRemoval = () => {
  return (
    <>
      <Helmet>
        <title>Snow Removal Services | Riekels Lawn Care & Landscaping</title>
        <meta
          name="description"
          content="Professional snow removal in the West Twin Cities Metro, MN. Residential and commercial plowing, sidewalk clearing, and ice control. Call (763) 245-7813."
        />
      </Helmet>
      <main>
        <PageHero
          badge="Our Services"
          title="Snow Removal"
          subtitle="Reliable snow and ice management to keep your property safe all winter."
        />

        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
              <div className="lg:col-span-1">
                <ServiceSidebar />
              </div>
              <div className="lg:col-span-3">
                <div className="mb-8 rounded-xl overflow-hidden shadow-lg">
                  <img 
                    src={snowImage} 
                    alt="Snow plow truck clearing a driveway" 
                    className="w-full h-64 md:h-80 object-cover object-center"
                  />
                </div>

                <div className="prose prose-lg max-w-none mb-12">
                  <h2 className="text-3xl font-heading font-bold text-foreground mb-6">
                    Minnesota Winter? We've Got You Covered
                  </h2>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    Minnesota winters are no joke. When the snow starts falling, you need a reliable team 
                    that will clear your driveway and walks so you can get on with your day.
                  </p>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    We offer both per-push service and seasonal contracts for residential and commercial 
                    properties. Our crews start early so your property is clear before you need to leave for work.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {features.map((feature, index) => (
                    <article key={index} className="feature-card">
                      <div className="icon-circle mb-4">
                        <feature.icon className="w-6 h-6" />
                      </div>
                      <h3 className="text-lg font-heading font-bold text-foreground mb-2">
                        {feature.title}
                      </h3>
                      <p className="text-muted-foreground">
                        {feature.description}
                      </p>
                    </article>
                  ))}
                </div>

                <div className="mt-12 bg-secondary rounded-2xl p-8 text-center">
                  <Snowflake className="w-12 h-12 mx-auto mb-4 text-secondary-foreground" />
                  <h3 className="text-2xl font-heading font-bold text-secondary-foreground mb-3">
                    Sign Up for Winter Service
                  </h3>
                  <p className="text-secondary-foreground/80 mb-6 max-w-lg mx-auto">
                    Spots fill up fast! Contact us now to get on our snow removal schedule.
                  </p>
                  <Link to="/contact">
                    <Button size="lg" className="bg-white hover:bg-white/90 text-secondary font-semibold">
                      Request a Quote <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <ServiceFAQ faqs={snowRemovalFaqs} />

        <CTASection />
      </main>
    </>
  );
};

export default SnowRemoval;
